import { CATEGORY_SLUGS, POST_CATEGORY_LABELS, categoryFromSlug, newsListFilter, type PostCategory } from '@hbac/contract';
import { Inbox } from 'lucide-react';
import { useEffect } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { NEWS_PAGE_SIZE, newsPagePath, parsePageParam } from '../lib/pagination';
import { usePrefetchPublicPosts, usePublicPosts } from '../services/public-queries';
import { BackHome } from './components/BackHome';
import { Pagination } from './components/Pagination';
import { PostCard } from './components/PostCard';
import { SITE_TITLE, useDocumentTitle } from '../hooks/use-document-title';

const FILTERS: Array<{ key: PostCategory | 'all'; label: string }> = [
  { key: 'all', label: 'Tất cả' },
  { key: 'tin-tuc', label: POST_CATEGORY_LABELS['tin-tuc'] },
  { key: 'thong-bao', label: POST_CATEGORY_LABELS['thong-bao'] },
  { key: 'su-kien', label: POST_CATEGORY_LABELS['su-kien'] },
];

/**
 * The news list, a fixed number of articles per page. /news is page 1,
 * /news/pages/N the others; the category filter rides in the query string
 * (?category=events) so a filtered page can be shared.
 */
export function NewsListPage() {
  const navigate = useNavigate();
  const { page: rawPage } = useParams();
  const [params, setParams] = useSearchParams();
  const raw = params.get('category');
  const category: PostCategory | 'all' = categoryFromSlug(raw) ?? 'all';
  const slug = category === 'all' ? null : CATEGORY_SLUGS[category];
  const page = parsePageParam(rawPage);
  useDocumentTitle(page && page > 1 ? `Tin tức, trang ${page} | ${SITE_TITLE}` : `Tin tức | ${SITE_TITLE}`);

  // An old link carrying the stored value (?category=tin-tuc) is rewritten to its slug.
  useEffect(() => {
    if (category !== 'all' && raw !== CATEGORY_SLUGS[category]) {
      setParams({ category: CATEGORY_SLUGS[category] }, { replace: true });
    }
  }, [category, raw, setParams]);

  // The same rule as the server's (postsFilterFor), so the list it put in the HTML is the one asked for here.
  const listFilter = newsListFilter(category === 'all' ? null : category, page ?? 1);
  const { data, isPending, isFetching, isPlaceholderData } = usePublicPosts(listFilter);

  const posts = data?.posts ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / NEWS_PAGE_SIZE));

  // The next page is fetched while the reader is on this one, so the click is instant.
  const prefetch = usePrefetchPublicPosts();
  useEffect(() => {
    if (page && page < pages) prefetch(newsListFilter(category === 'all' ? null : category, page + 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pages, category]);

  // /news/pages/abc or /news/pages/1: the one canonical address for that page.
  if (page === null || rawPage === '1') return <Navigate to={newsPagePath(1, slug)} replace />;
  // Past the last page (an old link after posts were removed): the last page.
  if (data && !isPlaceholderData && total > 0 && page > pages) return <Navigate to={newsPagePath(pages, slug)} replace />;

  const hrefFor = (p: number) => newsPagePath(p, slug);
  const pick = (key: PostCategory | 'all') => navigate(newsPagePath(1, key === 'all' ? null : CATEGORY_SLUGS[key]));

  return (
    <div className="mx-auto w-full max-w-[1328px] px-4 py-12 md:px-6 md:py-16">
      <div className="mb-4">
        <BackHome />
      </div>
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl dark:text-white">Tin tức</h1>
      <p className="mt-2 text-base text-slate-500 dark:text-slate-400">
        Hoạt động, thông báo và sự kiện của câu lạc bộ.
      </p>

      {/* One row on a phone, scrolled sideways, rather than two lines of chips. */}
      <div className="no-scrollbar -mx-4 mt-6 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => pick(f.key)}
            aria-pressed={category === f.key}
            className={`min-h-10 shrink-0 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400 ${
              category === f.key
                ? 'bg-brand-700 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-navy-800 dark:text-slate-300 dark:hover:bg-navy-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className={`mt-8 grid gap-5 transition-opacity sm:grid-cols-2 lg:grid-cols-3 ${isFetching && isPlaceholderData ? 'opacity-60' : ''}`}>
        {isPending
          ? [...Array(NEWS_PAGE_SIZE)].map((_, i) => (
              <div key={i} className="h-80 animate-pulse rounded-2xl bg-slate-100 dark:bg-navy-800" />
            ))
          : posts.map((p, i) => <PostCard key={p.slug} post={p} priority={page === 1 && i === 0} heading="h2" />)}
      </div>

      {!isPending && posts.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-20 text-slate-500 dark:text-slate-400">
          <Inbox size={36} strokeWidth={1.5} />
          <p className="text-sm">Chưa có bài viết nào trong mục này.</p>
        </div>
      )}

      <Pagination page={page} pages={pages} hrefFor={hrefFor} />
    </div>
  );
}
